/**
 * ProfileIcon Component
 *
 * Displays a user's profile icon as a circular cropped image.
 * Used throughout the app next to usernames in PvP and tournaments.
 *
 * Config format (use /icon-config-tool.html to tune values):
 * - scale: Zoom level (higher = more zoomed in on face)
 * - offsetY: Vertical offset in % (negative = move up to show face)
 * - offsetX: Horizontal offset in % (negative = move left)
 */

import React from 'react';

// Map icon keys to display names, image paths, and face positioning
// Use /icon-config-tool.html to adjust these values visually
// Unlock conditions:
// - null/undefined = always unlocked (starters, gastly)
// - 'tutorial-complete' = complete the tutorial
// - 'pvp-gold' = reach Gold League in PvP
// - 'collect-100' = collect 100 Pokemon
// - 'elite-four' = defeat the Elite 4 for the first time
const ICON_CONFIG = {
  'pikachu': { name: 'Pikachu', path: '/images/profile-icons/pikachu.png', scale: 1.4, offsetY: -7, offsetX: 20 },
  'squirtle': { name: 'Squirtle', path: '/images/profile-icons/squirtle.png', scale: 1.6, offsetY: 1, offsetX: 11 },
  'charmander': { name: 'Charmander', path: '/images/profile-icons/charmander.png', scale: 1.5, offsetY: -1, offsetX: 17 },
  'bulbasaur': { name: 'Bulbasaur', path: '/images/profile-icons/bulbasaur.png', scale: 1.3, offsetY: -11, offsetX: 13 },
  'mewtwo': { name: 'Mewtwo', path: '/images/profile-icons/mewtwo.png', scale: 1.5, offsetY: 0, offsetX: -11, unlock: 'tutorial-complete', unlockDesc: 'Complete the tutorial' },
  'gastly': { name: 'Gastly', path: '/images/profile-icons/gastly.png', scale: 1.1, offsetY: -8, offsetX: 7 },
  'officer-jenny': { name: 'Officer Jenny', path: '/images/profile-icons/officer-jenny.png', scale: 2.4, offsetY: 0, offsetX: -3, unlock: 'pvp-gold', unlockDesc: 'Reach Gold League in PvP' },
  'nurse-joy': { name: 'Nurse Joy', path: '/images/profile-icons/nurse-joy.png', scale: 1.6, offsetY: -1, offsetX: 0, unlock: 'collect-100', unlockDesc: 'Collect 100 Pokémon' },
  'delia-ketchum': { name: "Ash's Mom and Dad", path: '/images/profile-icons/delia-ketchum.png', scale: 1.8, offsetY: -2, offsetX: 0, unlock: 'elite-four', unlockDesc: 'Defeat the Elite 4' }
};

const ProfileIcon = ({ icon = 'pikachu', size = 32, className = '', showBorder = true }) => {
  const config = ICON_CONFIG[icon] || ICON_CONFIG.pikachu;
  const scale = config.scale || 1;
  const offsetY = config.offsetY || 0;
  const offsetX = config.offsetX || 0;

  return (
    <div
      className={`rounded-full overflow-hidden flex-shrink-0 relative ${showBorder ? 'ring-2 ring-white shadow-sm' : ''} ${className}`}
      style={{ width: size, height: size }}
    >
      {/*
        Don't use object-cover - it pre-crops tall images making it impossible
        to access the full image height via transforms. Instead, use width:100%
        with absolute positioning so the full image is available for positioning.
      */}
      <img
        src={config.path}
        alt={config.name}
        className="absolute top-0 left-0"
        style={{
          width: '100%',
          height: 'auto',
          transform: `scale(${scale}) translate(${offsetX}%, ${offsetY}%)`,
          transformOrigin: 'top center'
        }}
      />
    </div>
  );
};

export { ICON_CONFIG };
export default ProfileIcon;
